import type {
  EscopoMacroPlanejamento,
  PlanejamentoControle,
  TipoObraPlanejamento,
} from "@/modules/financeiro-obras";
import type { EscopoACancelar } from "./cancelar-escopo-macro-dialog";
import type { TipoACancelar } from "./cancelar-tipo-obra-dialog";
import {
  formatClassificacao,
  formatMilhoesComUnidade,
  formatMoeda,
} from "./planejamento-controle.format";

export const FEL_ZERO = "FEL-0";

export function transferenciaEmFelZero(felOrigem: string, felDestino: string | null) {
  return felOrigem === FEL_ZERO && felDestino === FEL_ZERO;
}

export function felComContingencia(fel: string) {
  return Number(/\d+/.exec(fel)?.[0] ?? 0) >= 4;
}

/** Escopo, o Tipo e o Centro de Custo a que pertence, na composição já carregada. */
export function localizarEscopo(planejamento: PlanejamentoControle, obraEscopoMacroId: string) {
  for (const centro of planejamento.centrosCusto) {
    for (const tipo of centro.tiposObra) {
      const escopo = tipo.escoposMacro.find((item) => item.id === obraEscopoMacroId);
      if (escopo) return { centro, tipo, escopo };
    }
  }
  return null;
}

/** O cancelamento só abre sem solicitação pendente e com o Escopo ainda ativo. */
export function cancelamentoBloqueado({
  situacao,
  solicitacaoPendente,
  edicaoProjecao,
}: EscopoMacroPlanejamento) {
  return (
    situacao === "CANCELADO" ||
    solicitacaoPendente !== "NENHUMA" ||
    edicaoProjecao.bloqueio === "SALDO_A_DESTINAR"
  );
}

export function montarEscopoACancelar(
  planejamento: PlanejamentoControle,
  obraEscopoMacroId: string,
): EscopoACancelar | null {
  const achado = localizarEscopo(planejamento, obraEscopoMacroId);
  if (!achado) return null;
  const { centro, tipo, escopo } = achado;

  return {
    obraEscopoMacroId,
    nome: escopo.nome,
    tipoObraCodigo: tipo.tipoObra.codigo,
    tipoObra: `${tipo.tipoObra.codigo} · ${tipo.tipoObra.nome}`,
    centroCusto: `${centro.codigo} · ${centro.nome}`,
    classificacao: formatClassificacao(escopo.classificacao),
    fel: planejamento.obra.fel,
    projecao:
      escopo.possuiProjecao && escopo.totais
        ? formatMilhoesComUnidade(escopo.totais.outlook.total)
        : null,
  };
}

/** Tipo, o Centro de Custo a que pertence, na composição já carregada. */
export function localizarTipo(planejamento: PlanejamentoControle, obraTipoObraId: string) {
  for (const centro of planejamento.centrosCusto) {
    const tipo = centro.tiposObra.find((item) => item.id === obraTipoObraId);
    if (tipo) return { centro, tipo };
  }
  return null;
}

/** O cancelamento do Tipo só abre sem solicitação pendente (do Tipo ou de algum Escopo) e com o Tipo ainda ativo. */
export function cancelamentoTipoBloqueado({
  situacao,
  solicitacaoPendente,
  escoposMacro,
}: TipoObraPlanejamento) {
  return (
    situacao !== "APROVADO" ||
    solicitacaoPendente !== "NENHUMA" ||
    escoposMacro.some((escopo) => escopo.solicitacaoPendente !== "NENHUMA")
  );
}

function valorTotalDoTipo(planejamento: PlanejamentoControle, obraTipoObraId: string) {
  const totais = planejamento.linhas.find(
    (linha) => linha.chave === `tipo:${obraTipoObraId}`,
  )?.totais;
  return totais ? formatMoeda(totais.outlook.total, "BRL") : null;
}

export function montarTipoACancelar(
  planejamento: PlanejamentoControle,
  obraTipoObraId: string,
): TipoACancelar | null {
  const achado = localizarTipo(planejamento, obraTipoObraId);
  if (!achado) return null;
  const { centro, tipo } = achado;

  return {
    obraTipoObraId,
    codigo: tipo.tipoObra.codigo,
    nome: tipo.tipoObra.nome,
    classificacao: formatClassificacao(tipo.classificacao),
    centroCusto: `${centro.codigo} · ${centro.nome}`,
    obra: `Obra ${planejamento.obra.codigo}`,
    fel: planejamento.obra.fel,
    valorTotal: valorTotalDoTipo(planejamento, obraTipoObraId),
    embarcacao: planejamento.obra.embarcacao?.nome ?? null,
    classificacaoEscopos: Object.fromEntries(
      tipo.escoposMacro.map((escopo) => [escopo.id, formatClassificacao(escopo.classificacao)]),
    ),
  };
}
